import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  generateVideo,
  checkVideoStatus,
  downloadVideo,
  getVideoHistory,
  deleteVideoRecord,
  calculateCost,
  downloadVideoFile,
  VideoHistoryItem
} from '../../services/videoService';
import PageHeader from '../common/PageHeader';
import './Videos.css';

interface VideosProps {
  onToggleSidebar: () => void;
  onClose: () => void;
}

const Videos: React.FC<VideosProps> = ({ onToggleSidebar, onClose }) => {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'create' | 'history'>('create');
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState<'16:9' | '9:16' | '1:1'>('16:9');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [videoHistory, setVideoHistory] = useState<VideoHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [downloadingVideos, setDownloadingVideos] = useState<Set<string>>(new Set());
  const [modalVideo, setModalVideo] = useState<{ url: string; prompt: string } | null>(null);
  const [expandedVideos, setExpandedVideos] = useState<Set<string>>(new Set());
  const [loadedVideoUrls, setLoadedVideoUrls] = useState<Map<string, string>>(new Map());

  // Load video history when user is authenticated
  useEffect(() => {
    if (!authLoading && user) {
      loadHistory();
    }
  }, [authLoading, user]);

  // Debug: Log when videoHistory changes
  useEffect(() => {
    console.log('videoHistory state updated:', videoHistory);
  }, [videoHistory]);

  // Auto-refresh history for pending/processing videos
  useEffect(() => {
    if (!user || videoHistory.length === 0) return;

    const hasProcessingVideos = videoHistory.some(
      video => video.status === 'pending' || video.status === 'processing'
    );

    if (!hasProcessingVideos) return;

    // Poll every 5 seconds with autoUpdate enabled
    const pollInterval = setInterval(() => {
      console.log('Auto-refreshing video history with status updates...');
      loadHistory(true); // Enable autoUpdate for status checking
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [user, videoHistory]);

  const loadHistory = async (autoUpdate: boolean = false) => {
    setLoadingHistory(true);
    try {
      const response = await getVideoHistory(20, 0, autoUpdate);
      console.log('Video history response:', response);
      // Response structure: { success, message, data: { videos, total, limit, offset } }
      if (response.success && response.data?.videos) {
        console.log('Setting video history:', response.data.videos);
        setVideoHistory(response.data.videos);
      } else {
        console.log('Response not successful or no videos:', response);
      }
    } catch (err: any) {
      console.error('Failed to load video history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (prompt.length < 10) {
      setError('Prompt must be at least 10 characters long');
      return;
    }

    if (prompt.length > 500) {
      setError('Prompt must be less than 500 characters');
      return;
    }

    setError(null);
    setSuccess(null);
    setIsGenerating(true);

    try {
      const response = await generateVideo({
        prompt,
        duration,
        aspectRatio
      });

      if (response.success && response.data) {
        setSuccess(`Video generation started! Estimated cost: ${response.data.estimatedCost}`);

        // Clear form
        setPrompt('');
        
        // Reload history to show the new video
        await loadHistory();
        
        // Switch to history tab
        setActiveTab('history');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start video generation');
    } finally {
      setIsGenerating(false);
    }
  };


  const handleDownloadToDevice = async (operationId: string, prompt: string) => {
    try {
      setDownloadingVideos(prev => new Set(prev).add(operationId));
      const response = await downloadVideo(operationId);
      
      if (response.success && response.data) {
        const filename = `video_${operationId.substring(0, 8)}_${Date.now()}.mp4`;
        downloadVideoFile(response.data.firebaseUrl, filename);
        setSuccess('Video downloaded successfully!');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to download video');
    } finally {
      setDownloadingVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationId);
        return newSet;
      });
    }
  };

  const handleToggleVideo = async (operationId: string) => {
    // If already expanded, collapse it
    if (expandedVideos.has(operationId)) {
      setExpandedVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationId);
        return newSet;
      });
      return;
    }

    // If video URL is already loaded, just expand
    if (loadedVideoUrls.has(operationId)) {
      setExpandedVideos(prev => new Set(prev).add(operationId));
      return;
    }

    // Otherwise, load the video URL first
    try {
      setDownloadingVideos(prev => new Set(prev).add(operationId));
      const response = await downloadVideo(operationId);
      
      if (response.success && response.data?.firebaseUrl) {
        // Store the URL
        setLoadedVideoUrls(prev => new Map(prev).set(operationId, response.data!.firebaseUrl));
        // Expand the video
        setExpandedVideos(prev => new Set(prev).add(operationId));
      } else {
        setError('Failed to load video: No URL returned from server');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load video');
    } finally {
      setDownloadingVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationId);
        return newSet;
      });
    }
  };

  const handleViewVideo = async (operationId: string, prompt: string) => {
    try {
      console.log('handleViewVideo called with operationId:', operationId);
      setDownloadingVideos(prev => new Set(prev).add(operationId));
      const response = await downloadVideo(operationId);
      
      console.log('Download video response:', response);
      console.log('Response data:', response.data);
      console.log('Firebase URL:', response.data?.firebaseUrl);
      
      if (response.success && response.data?.firebaseUrl) {
        console.log('Setting modal video with URL:', response.data.firebaseUrl);
        // Open video in modal
        setModalVideo({
          url: response.data.firebaseUrl,
          prompt: prompt
        });
        setSuccess('Video loaded successfully');
      } else {
        console.error('Response missing firebaseUrl:', response);
        setError('Failed to load video: No URL returned from server');
      }
    } catch (err: any) {
      console.error('Error loading video:', err);
      setError(err.message || 'Failed to load video');
    } finally {
      setDownloadingVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationId);
        return newSet;
      });
    }
  };

  const handleCloseModal = () => {
    setModalVideo(null);
  };

  const handleDelete = async (operationId: string) => {
    if (!window.confirm('Are you sure you want to delete this video record?')) {
      return;
    }

    try {
      await deleteVideoRecord(operationId);
      setSuccess('Video record deleted');
      loadHistory();
    } catch (err: any) {
      setError(err.message || 'Failed to delete video record');
    }
  };

  const handleCheckStatus = async (operationId: string) => {
    try {
      const response = await checkVideoStatus(operationId);
      if (response.success && response.data) {
        alert(`Status: ${response.data.status}\nDone: ${response.data.done ? 'Yes' : 'No'}`);
        if (response.data.done) {
          loadHistory();
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to check video status');
    }
  };

  const estimatedCost = calculateCost(duration, 'fast');
  const charCount = prompt.length;

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="videos-container">
        <div className="videos-header">
          <h1 className="videos-title">Video Generation</h1>
          <p className="videos-subtitle">Loading...</p>
        </div>
      </div>
    );
  }

  // Show message if user is not authenticated
  if (!user) {
    return (
      <div className="videos-container">
        <div className="videos-header">
          <h1 className="videos-title">Video Generation</h1>
          <p className="videos-subtitle">Please sign in to generate videos</p>
        </div>
        <div className="empty-state">
          <p>Authentication required to access video generation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="videos-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/videos</h1>}
      />
      
      <div className="videos-content">
      <div className="videos-header">
        <p className="videos-subtitle">Create AI-generated videos using Google Veo 3.1</p>
      </div>

      {error && (
        <div className="videos-message videos-error">
          <span className="message-icon">⚠️</span>
          <span>{error}</span>
          <button className="message-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="videos-message videos-success">
          <span className="message-icon">✓</span>
          <span>{success}</span>
          <button className="message-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <div className="videos-tabs">
        <button
          className={`videos-tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          Create Video
        </button>
        <button
          className={`videos-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {activeTab === 'create' && (
      <div className="videos-generator">
        <div className="generator-section">
          <label className="generator-label">
            <span className="label-text">Video Prompt</span>
            <span className="label-info">{charCount}/500</span>
          </label>
          <textarea
            className="generator-textarea"
            placeholder="Describe the video you want to generate... (e.g., 'A serene beach at sunset with gentle waves')"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={500}
            rows={4}
          />
        </div>

        <div className="generator-controls">
          <div className="control-group">
            <label className="generator-label">
              <span className="label-text">Duration (seconds)</span>
            </label>
            <input
              type="range"
              className="duration-slider"
              min="5"
              max="60"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
            />
            <div className="duration-display">{duration}s</div>
          </div>

          <div className="control-group">
            <label className="generator-label">
              <span className="label-text">Aspect Ratio</span>
            </label>
            <select
              className="aspect-ratio-select"
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value as '16:9' | '9:16' | '1:1')}
            >
              <option value="16:9">16:9 (Landscape)</option>
              <option value="9:16">9:16 (Portrait)</option>
              <option value="1:1">1:1 (Square)</option>
            </select>
          </div>
        </div>

        <div className="generator-cost">
          <span className="cost-label">Estimated Cost:</span>
          <span className="cost-value">{estimatedCost}</span>
          <span className="cost-info">(Veo 3.1 Fast @ $0.40/sec)</span>
        </div>

        <button
          className="generator-button"
          onClick={handleGenerate}
          disabled={isGenerating || prompt.length < 10}
        >
          {isGenerating ? 'Starting...' : 'Generate Video'}
        </button>
      </div>
      )}

      {activeTab === 'history' && (
      <div className="videos-section">
        <div className="section-header">
          <button className="refresh-button" onClick={() => loadHistory(false)} disabled={loadingHistory}>
            {loadingHistory ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {videoHistory.length === 0 && !loadingHistory && (
          <div className="empty-state">
            <p>No videos generated yet</p>
          </div>
        )}

        {videoHistory.length > 0 && (
          <div className="videos-list">
            {videoHistory.map((video) => (
              <div key={video.id} className={`video-card ${video.status} ${expandedVideos.has(video.operation_id) ? 'expanded' : ''}`}>
                <div className="video-header">
                  <span className="video-prompt">{video.prompt.substring(0, 80)}...</span>
                  <span className={`video-badge ${video.status}`}>
                    {video.status}
                    {(video.status === 'pending' || video.status === 'processing') && ' ⏳'}
                  </span>
                </div>
                <div className="video-meta">
                  <span>{video.duration}s</span>
                  <span>{video.aspect_ratio}</span>
                  <span className="video-cost">{video.estimatedCost}</span>
                  <span className="video-date">{new Date(video.created_at).toLocaleDateString()}</span>
                </div>
                {(video.status === 'pending' || video.status === 'processing') && (
                  <div className="video-progress-bar">
                    <div className="progress-fill animating"></div>
                  </div>
                )}
                
                {/* Inline Video Player for Completed Videos */}
                {video.status === 'completed' && expandedVideos.has(video.operation_id) && loadedVideoUrls.has(video.operation_id) && (
                  <div className="inline-video-container">
                    <video 
                      controls 
                      src={loadedVideoUrls.get(video.operation_id)} 
                      className="inline-video-player"
                      crossOrigin="anonymous"
                    />
                  </div>
                )}
                
                <div className="video-actions">
                  {video.status === 'completed' && (
                    <>
                      <button
                        className={`action-button toggle ${expandedVideos.has(video.operation_id) ? 'expanded' : ''}`}
                        onClick={() => handleToggleVideo(video.operation_id)}
                        disabled={downloadingVideos.has(video.operation_id)}
                      >
                        {downloadingVideos.has(video.operation_id) 
                          ? 'Loading...' 
                          : expandedVideos.has(video.operation_id) 
                            ? '▲ Hide Video' 
                            : '▼ Show Video'}
                      </button>
                      <button
                        className="action-button fullscreen"
                        onClick={() => handleViewVideo(video.operation_id, video.prompt)}
                        disabled={downloadingVideos.has(video.operation_id)}
                      >
                        Fullscreen
                      </button>
                      <button
                        className="action-button download"
                        onClick={() => handleDownloadToDevice(video.operation_id, video.prompt)}
                        disabled={downloadingVideos.has(video.operation_id)}
                      >
                        {downloadingVideos.has(video.operation_id) ? 'Downloading...' : 'Download'}
                      </button>
                    </>
                  )}
                  {(video.status === 'pending' || video.status === 'processing') && (
                    <button
                      className="action-button status"
                      onClick={() => handleCheckStatus(video.operation_id)}
                    >
                      Check Status
                    </button>
                  )}
                  <button
                    className="action-button delete"
                    onClick={() => handleDelete(video.operation_id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Video Modal */}
      {modalVideo && (
        <div className="video-modal-overlay" onClick={handleCloseModal}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3 className="video-modal-title">{modalVideo.prompt}</h3>
              <button className="video-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="video-modal-body">
              <video 
                controls 
                autoPlay
                src={modalVideo.url} 
                className="video-modal-player"
                crossOrigin="anonymous"
                onError={(e) => {
                  console.error('Video loading error:', e);
                  console.log('Failed URL:', modalVideo.url);
                }}
                onCanPlay={() => console.log('Video loaded successfully')}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Videos;

